import React, { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import BudgetHeader from 'routes/BudgetModule/components/shared/BudgetHeader/BudgetHeader';
import { useGetBudgetProposals } from 'routes/BudgetModule/helpers/hooks/budgetProposals';
import Loading from 'components/Loading';
import { useSearchParams } from 'hooks/searchParams';
import BudgetProposalsList from './BudgetProposalsList';
import BudgetLibraryFilters from './BudgetFilters/BudgetLibraryFilters';
import {
    filterBudgetProposalsByExercice,
    getSortedBudgetNamesWithArchivedStatusByYear,
    getExercicesOfBudget,
    sortBudgetProposals,
} from './helper';
import BudgetContent from '../BudgetContent';
import NoDataState from 'ui/NoDataState/NoDataState';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Box } from '@mui/material';
import GlobalExport from './GlobalExport';

const StyActionBar = styled(Box, { name: 'StyActionBar' })({
    display: 'flex',
    justifyContent: 'space-between',
});

export default function BudgetLibrary(): React.ReactElement {
    const { t } = useTranslation();
    const { data: budgetProposals, isLoading } = useGetBudgetProposals();
    const exercices = useMemo(
        () => getExercicesOfBudget(budgetProposals!),
        [budgetProposals],
    );
    const [selectedBudgetSoftwareIds, setSelectedBudgetSoftwareIds] = useState<
        string[]
    >([]);

    const {
        exercice: [exerciceParam, setSelectedExercice],
    } = useSearchParams(['exercice']);

    const selectedExercice = !Number.isNaN(parseInt(exerciceParam ?? '', 10))
        ? parseInt(exerciceParam ?? '', 10)
        : Math.max(...exercices);

    useEffect(() => {
        if (!exerciceParam && exercices.length > 0) {
            setSelectedExercice(selectedExercice.toString());
        }
    }, [exerciceParam, exercices, selectedExercice, setSelectedExercice]);

    const budgetNamesByYear = getSortedBudgetNamesWithArchivedStatusByYear(
        budgetProposals ?? [],
    );

    const filteredBudgetProposals = useMemo(
        () =>
            filterBudgetProposalsByExercice(
                budgetProposals ?? [],
                selectedExercice ? selectedExercice.toString() : 'no-exercice',
            ),
        [budgetProposals, selectedExercice],
    );
    useEffect(() => {
        const selectedDisplayedBudgetSoftwareId = _(filteredBudgetProposals)
            .filter((bp) => bp.displayPosition !== null)
            .map((bp) => bp.budgetSoftwareId ?? '')
            .uniq()
            .value();

        setSelectedBudgetSoftwareIds(selectedDisplayedBudgetSoftwareId);
    }, [filteredBudgetProposals]);

    const sortedBudgetProposals = filteredBudgetProposals
        .filter((bp) => bp.displayPosition !== null)
        .sort(sortBudgetProposals);

    const pageContent = (
        <>
            <StyActionBar>
                <BudgetLibraryFilters
                    selectedBudgetSoftwareIds={selectedBudgetSoftwareIds}
                    setSelectedBudgetSoftwareIds={setSelectedBudgetSoftwareIds}
                    budgetNamesByYear={budgetNamesByYear}
                    selectedExercice={selectedExercice}
                    setSelectedExercice={setSelectedExercice}
                    exerciceOptions={exercices}
                />
                <GlobalExport budgetProposals={filteredBudgetProposals} />
            </StyActionBar>
            <BudgetProposalsList
                sortedBudgetProposals={sortedBudgetProposals}
            />
        </>
    );
    if (isLoading || !budgetProposals) {
        return <Loading fullWidth />;
    }
    return (
        <div className="page-container dx-viewport">
            <BudgetHeader
                headerTitle={t('budgetProposal.headerTitle')}
                canShowFinanceSoftwareSelector
                exercice={selectedExercice}
            />
            <BudgetContent>
                {budgetProposals?.length === 0 ? (
                    <NoDataState message="budgetProposal.noDataText" />
                ) : (
                    pageContent
                )}
            </BudgetContent>
        </div>
    );
}
